/**
 * AzureDevOpsImport Component
 * 
 * A wizard-style dialog for importing data from Azure DevOps.
 * Only visible when the Azure DevOps addon is enabled by admin.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../utils/backendURL';
import { showSuccess, showError, showWarning } from '../utils/notifications';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { 
  Cloud, 
  Loader2, 
  CheckCircle, 
  XCircle, 
  ArrowRight,
  ArrowLeft,
  Download,
  FolderOpen,
  FileText,
  TestTube,
  ListOrdered,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const AzureDevOpsImport = ({ open, onClose, onImportComplete, existingProjects = [] }) => {
  // Wizard state
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Azure DevOps data
  const [azureProjects, setAzureProjects] = useState([]);
  const [selectedAzureProject, setSelectedAzureProject] = useState(null);
  
  // Import options
  const [targetProjectId, setTargetProjectId] = useState('');
  const [importOptions, setImportOptions] = useState({
    work_items: true,
    test_plans: true,
    test_cases: true
  });
  
  // Preview data
  const [previewData, setPreviewData] = useState(null);
  
  // Import result
  const [importResult, setImportResult] = useState(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setStep(1);
      setSelectedAzureProject(null);
      setTargetProjectId('');
      setImportOptions({ work_items: true, test_plans: true, test_cases: true });
      setPreviewData(null);
      setImportResult(null);
      fetchAzureProjects();
    }
  }, [open]);

  const fetchAzureProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/addons/azure-devops/projects`,
        { headers: getAuthHeaders() }
      );
      setAzureProjects(response.data.projects || []);
    } catch (error) {
      console.error('Failed to fetch Azure DevOps projects:', error);
      if (error.response?.status === 400) {
        showError('Azure DevOps addon not configured. Please configure it in User Settings → Addons.');
      } else {
        showError('Failed to connect to Azure DevOps. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPreviewData = async () => {
    if (!selectedAzureProject) return;
    
    try {
      setLoading(true);
      
      const preview = {
        work_items: { count: 0, sample: [] },
        test_plans: { count: 0, sample: [] },
        test_cases: { count: 0, sample: [] }
      };
      
      if (importOptions.work_items) {
        try {
          const response = await axios.get(
            `${BACKEND_URL}/api/addons/azure-devops/projects/${encodeURIComponent(selectedAzureProject.name)}/work-items`,
            { headers: getAuthHeaders() }
          );
          preview.work_items = {
            count: response.data.total || response.data.work_items?.length || 0,
            sample: (response.data.work_items || []).slice(0, 5)
          };
        } catch (e) {
          console.error('Failed to fetch work items preview:', e);
        }
      }
      
      if (importOptions.test_plans) {
        try {
          const response = await axios.get(
            `${BACKEND_URL}/api/addons/azure-devops/projects/${encodeURIComponent(selectedAzureProject.name)}/test-plans`,
            { headers: getAuthHeaders() }
          );
          preview.test_plans = {
            count: response.data.test_plans?.length || 0,
            sample: (response.data.test_plans || []).slice(0, 5)
          };
        } catch (e) {
          console.error('Failed to fetch test plans preview:', e);
        }
      }
      
      setPreviewData(preview);
    } catch (error) {
      console.error('Failed to fetch preview data:', error);
      showError('Failed to load preview data');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!selectedAzureProject) return;
    
    try {
      setLoading(true);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/addons/azure-devops/import`,
        {
          azure_project_id: selectedAzureProject.id,
          azure_project_name: selectedAzureProject.name,
          target_project_id: targetProjectId || null,
          import_work_items: importOptions.work_items,
          import_test_plans: importOptions.test_plans,
          import_test_cases: importOptions.test_cases
        },
        { headers: getAuthHeaders() }
      );
      
      setImportResult(response.data);
      setStep(4);
      
      if (response.data.stats.errors?.length === 0) {
        showSuccess('Import completed successfully!');
      } else {
        showWarning('Import completed with some errors');
      }
      
      if (onImportComplete) {
        onImportComplete(response.data.target_project_id);
      }
    } catch (error) {
      console.error('Import failed:', error);
      showError(error.response?.data?.detail || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (step === 1 && !selectedAzureProject) {
      showError('Please select an Azure DevOps project');
      return;
    }
    
    if (step === 2) {
      fetchPreviewData();
    }
    
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
  };

  // Step 1: Select Azure DevOps Project
  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Select Azure DevOps Project</h3>
        <Button variant="ghost" size="sm" onClick={fetchAzureProjects} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : azureProjects.length === 0 ? (
        <div className="text-center py-12">
          <Cloud className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No projects found in Azure DevOps</p>
          <p className="text-sm text-gray-500 mt-2">
            Make sure your PAT token has the correct permissions
          </p>
        </div>
      ) : (
        <div className="grid gap-3 max-h-80 overflow-y-auto">
          {azureProjects.map((project) => (
            <Card
              key={project.id}
              className={`p-4 cursor-pointer transition-all ${
                selectedAzureProject?.id === project.id
                  ? 'ring-2 ring-blue-500 bg-blue-50'
                  : 'hover:bg-gray-50'
              }`}
              onClick={() => setSelectedAzureProject(project)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">{project.name}</h4>
                  {project.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {project.description}
                    </p>
                  )}
                </div>
                {selectedAzureProject?.id === project.id && (
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  // Step 2: Configure Import Options
  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Import Options</h3>
        <p className="text-sm text-gray-500">
          Importing from: <strong>{selectedAzureProject?.name}</strong>
        </p>
      </div>
      
      {/* Target Project Selection */}
      <div className="space-y-3">
        <Label>Target Project in Lexa</Label>
        {existingProjects.length === 0 ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>No projects exist yet.</strong> A new project will be created automatically 
              based on the Azure DevOps project name.
            </p>
          </div>
        ) : (
          <select
            value={targetProjectId}
            onChange={(e) => setTargetProjectId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Create new project</option>
            {existingProjects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.icon} {project.name}
              </option>
            ))}
          </select>
        )}
      </div>
      
      {/* Data Type Selection */}
      <div className="space-y-3">
        <Label>Data to Import</Label>
        <div className="space-y-3">
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
            <Checkbox
              checked={importOptions.work_items}
              onCheckedChange={(checked) => setImportOptions(prev => ({ ...prev, work_items: checked }))}
            />
            <div className="flex items-center space-x-2">
              <FileText className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-medium">Work Items</p>
                <p className="text-xs text-gray-500">User Stories, Bugs, Features → Requirements</p>
              </div>
            </div>
          </label>
          
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
            <Checkbox
              checked={importOptions.test_plans}
              onCheckedChange={(checked) => setImportOptions(prev => ({ ...prev, test_plans: checked }))}
            />
            <div className="flex items-center space-x-2">
              <FolderOpen className="h-5 w-5 text-purple-600" />
              <div>
                <p className="font-medium">Test Plans & Suites</p>
                <p className="text-xs text-gray-500">Test Suites → Test Scenarios</p>
              </div>
            </div>
          </label>
          
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
            <Checkbox
              checked={importOptions.test_cases}
              onCheckedChange={(checked) => setImportOptions(prev => ({ ...prev, test_cases: checked }))}
            />
            <div className="flex items-center space-x-2">
              <TestTube className="h-5 w-5 text-green-600" />
              <div>
                <p className="font-medium">Test Cases</p>
                <p className="text-xs text-gray-500">Test Cases with Steps → Test Cases + Test Steps</p>
              </div>
            </div>
          </label>
        </div>
      </div>
    </div>
  );

  // Step 3: Preview
  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Preview Import</h3>
        <p className="text-sm text-gray-500">
          Review what will be imported from Azure DevOps
        </p>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Fetching preview...</span>
        </div>
      ) : previewData ? (
        <div className="space-y-4">
          {importOptions.work_items && (
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <span className="font-medium">Work Items</span>
                </div>
                <Badge variant="outline">{previewData.work_items.count} items</Badge>
              </div>
              {previewData.work_items.sample.length > 0 && (
                <div className="space-y-2">
                  {previewData.work_items.sample.map((item, idx) => (
                    <div key={idx} className="text-sm text-gray-600 flex items-center space-x-2">
                      <span className="bg-gray-100 px-2 py-0.5 rounded text-xs">{item.type}</span>
                      <span className="truncate">{item.title}</span>
                    </div>
                  ))}
                  {previewData.work_items.count > 5 && (
                    <p className="text-xs text-gray-400">
                      ...and {previewData.work_items.count - 5} more
                    </p>
                  )}
                </div>
              )}
            </Card>
          )}
          
          {importOptions.test_plans && (
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <FolderOpen className="h-5 w-5 text-purple-600" />
                  <span className="font-medium">Test Plans</span>
                </div>
                <Badge variant="outline">{previewData.test_plans.count} plans</Badge>
              </div>
              {previewData.test_plans.sample.length > 0 && (
                <div className="space-y-2">
                  {previewData.test_plans.sample.map((item, idx) => (
                    <div key={idx} className="text-sm text-gray-600">
                      {item.name}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
          
          {/* Summary */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Ready to import</p>
                <p className="mt-1">
                  This will create or update items in your Lexa project. 
                  Existing items (matched by Azure DevOps ID) will be updated.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          No preview data available
        </div>
      )}
    </div>
  );

  // Step 4: Results
  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center">
        {importResult?.stats.errors?.length === 0 ? (
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
        ) : (
          <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
        )}
        <h3 className="text-xl font-medium">Import Complete</h3>
      </div>
      
      {importResult && (
        <div className="space-y-4">
          <Card className="p-4">
            <h4 className="font-medium mb-3">Import Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Requirements Created:</span>
                <span className="font-medium">{importResult.stats.requirements_created}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Requirements Updated:</span>
                <span className="font-medium">{importResult.stats.requirements_updated}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Scenarios Created:</span>
                <span className="font-medium">{importResult.stats.scenarios_created}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Scenarios Updated:</span>
                <span className="font-medium">{importResult.stats.scenarios_updated}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Test Cases Created:</span>
                <span className="font-medium">{importResult.stats.test_cases_created}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Test Cases Updated:</span>
                <span className="font-medium">{importResult.stats.test_cases_updated}</span>
              </div>
            </div>
          </Card>
          
          {importResult.stats.errors?.length > 0 && (
            <Card className="p-4 border-red-200 bg-red-50">
              <h4 className="font-medium text-red-800 mb-2">Errors</h4>
              <ul className="text-sm text-red-700 space-y-1">
                {importResult.stats.errors.map((error, idx) => (
                  <li key={idx}>• {error}</li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}
    </div>
  );

  // Render current step
  const renderCurrentStep = () => {
    switch (step) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      default: return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Cloud className="h-5 w-5 text-blue-600" />
            <span>Import from Azure DevOps</span>
          </DialogTitle>
          <DialogDescription>
            Import work items, test plans, and test cases from your Azure DevOps project
          </DialogDescription>
        </DialogHeader>
        
        {/* Progress Indicator */}
        <div className="flex items-center justify-center space-x-2 py-4">
          {[1, 2, 3, 4].map((s) => (
            <React.Fragment key={s}>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= s
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step > s ? <CheckCircle className="h-4 w-4" /> : s}
              </div>
              {s < 4 && (
                <div className={`w-12 h-1 rounded ${step > s ? 'bg-blue-600' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
        
        {/* Step Content */}
        <div className="min-h-[300px]">
          {renderCurrentStep()}
        </div>
        
        {/* Footer */}
        <DialogFooter className="flex justify-between">
          <div>
            {step > 1 && step < 4 && (
              <Button variant="outline" onClick={handleBack} disabled={loading}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
          </div>
          
          <div className="flex space-x-2">
            {step < 4 ? (
              <>
                <Button variant="outline" onClick={onClose} disabled={loading}>
                  Cancel
                </Button>
                {step < 3 ? (
                  <Button onClick={handleNext} disabled={loading || (step === 1 && !selectedAzureProject)}>
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button 
                    onClick={handleImport} 
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Start Import
                      </>
                    )}
                  </Button>
                )}
              </>
            ) : (
              <Button onClick={onClose} className="bg-green-600 hover:bg-green-700">
                <CheckCircle className="h-4 w-4 mr-2" />
                Done
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AzureDevOpsImport;
